/**
 * Form for creating or editing an asset (domain, IP, IP range, URL).
 * Includes client-side validation for each asset type's value format.
 * Optionally attaches credentials at creation time.
 */

import { useState, type FormEvent } from 'react';
import type { AssetType, AssetCreateRequest } from '@/types/asset';
import type { CredentialSummary } from '@/types/credential';
import { isValidDomain, isValidIPv4, isValidIPv6, isValidCIDR, isValidUrl } from '@/utils/validators';

interface AssetFormProps {
  onSubmit: (payload: AssetCreateRequest, credentialIds: string[]) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  availableCredentials?: CredentialSummary[];
  credentialsEnabled?: boolean;
}

const assetTypes: { value: AssetType; label: string; placeholder: string }[] = [
  { value: 'domain', label: 'Domain', placeholder: 'example.com' },
  { value: 'ipv4', label: 'IPv4 Address', placeholder: '192.168.1.1' },
  { value: 'ipv6', label: 'IPv6 Address', placeholder: '::1' },
  { value: 'ip_range', label: 'IP Range (CIDR)', placeholder: '10.0.0.0/24' },
  { value: 'url', label: 'URL', placeholder: 'https://example.com' },
];

const TYPE_LABELS: Record<string, string> = {
  ssh_password: 'SSH Password',
  ssh_key: 'SSH Key',
  winrm_password: 'WinRM',
  snmp: 'SNMP v1/v2c',
  snmpv3: 'SNMPv3',
};

export default function AssetForm({ onSubmit, onCancel, isSubmitting, availableCredentials = [], credentialsEnabled=true }: AssetFormProps) {
  const [label, setLabel] = useState('');
  const [assetType, setAssetType] = useState<AssetType>('domain');
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [selectedCredIds, setSelectedCredIds] = useState<Set<string>>(new Set());

  /** Validate the asset value matches its declared type. */
  function validateValue(): boolean {
    switch (assetType) {
      case 'domain':
        if (!isValidDomain(value)) { setError('Invalid domain name.'); return false; }
        break;
      case 'ipv4':
        if (!isValidIPv4(value)) { setError('Invalid IPv4 address.'); return false; }
        break;
      case 'ipv6':
        if (!isValidIPv6(value)) { setError('Invalid IPv6 address.'); return false; }
        break;
      case 'ip_range':
        if (!isValidCIDR(value)) { setError('Invalid CIDR range (e.g., 10.0.0.0/24).'); return false; }
        break;
      case 'url':
        if (!isValidUrl(value)) { setError('Invalid URL.'); return false; }
        break;
    }
    return true;
  }

  function toggleCred(id: string) {
    setSelectedCredIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!value.trim()) { setError('Value is required.'); return; }
    if (!validateValue()) return;

    onSubmit(
      { asset_type: assetType, value: value.trim(), label: label.trim() || undefined },
      Array.from(selectedCredIds),
    );
  }

  const currentType = assetTypes.find((t) => t.value === assetType);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div>
        <label htmlFor="asset-label" className="block text-sm font-medium text-gray-700">Label (optional)</label>
        <input
          id="asset-label"
          className="input-field mt-1"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Production Web Server"
        />
      </div>

      <div>
        <label htmlFor="asset-type" className="block text-sm font-medium text-gray-700">Asset Type</label>
        <select
          id="asset-type"
          className="input-field mt-1"
          value={assetType}
          onChange={(e) => setAssetType(e.target.value as AssetType)}
        >
          {assetTypes.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="asset-value" className="block text-sm font-medium text-gray-700">Value</label>
        <input
          id="asset-value"
          className="input-field mt-1"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={currentType?.placeholder}
        />
      </div>

      {/* Credential attachment */}
      {credentialsEnabled && <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Credentials <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        {availableCredentials.length === 0 ? (
          <p className="text-xs text-gray-400">
            No credentials yet. You can add them after saving, or create credentials first in Settings → Credentials.
          </p>
        ) : (
          <div className="space-y-1.5 rounded-md border border-gray-200 p-2">
            {availableCredentials.map((cred) => (
              <label key={cred.id} className="flex items-center gap-2.5 cursor-pointer rounded px-2 py-1 hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={selectedCredIds.has(cred.id)}
                  onChange={() => toggleCred(cred.id)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-800">{cred.name}</span>
                <span className="text-xs text-gray-400">{TYPE_LABELS[cred.credential_type] ?? cred.credential_type}</span>
              </label>
            ))}
          </div>
        )}
      </div>}

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancel
        </button>
        <button type="submit" disabled={isSubmitting} className="btn-primary">
          {isSubmitting ? 'Adding...' : 'Add Asset'}
        </button>
      </div>
    </form>
  );
}
